import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import * as Sentry from '@sentry/react';
import { authAPI } from '../services/apiService';
import { tokenManager, setForceLogoutCallback, setGetLoginPathCallback } from '../config/apiConfig';
import { useCompany } from './CompanyContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signalRConnection, setSignalRConnection] = useState(null);
  const { company, companyCode, isResolved, isInitializing: isCompanyInitializing } = useCompany();

  // Refs for reconnection logic
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isReconnectingRef = useRef(false);
  const connectionRef = useRef(null); // Keep a ref to current connection for event handlers
  const dispatcherIdRef = useRef(null); // Track current dispatcher ID

  // Define forceLogout function
  const forceLogout = async () => {
    await stopSignalR();
    setSignalRConnection(null);
    Sentry.setUser(null);
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem(tokenManager.getAuthKey());
    tokenManager.removeToken();
  };

  // Register forceLogout and login path getter with API config on mount
  useEffect(() => {
    setForceLogoutCallback(forceLogout);
    setGetLoginPathCallback(() => '/login');
    return () => {
      setForceLogoutCallback(null);
      setGetLoginPathCallback(null);
    };
  }, []);

  useEffect(() => {
    // Wait for CompanyContext to finish its localStorage check before deciding
    if (isCompanyInitializing) {
      return;
    }
    // Only attempt to restore auth once company is resolved
    if (!isResolved) {
      setLoading(false);
      return;
    }

    // Check if user is already logged in using namespaced keys
    const savedAuth = localStorage.getItem(tokenManager.getAuthKey());
    if (savedAuth) {
      const authData = JSON.parse(savedAuth);

      // CRITICAL: Validate JWT token exists
      const token = tokenManager.getToken();
      if (!token || !authData.user?.token) {
        console.error('⚠️ Auth data exists but JWT token is missing - logging out');
        localStorage.removeItem(tokenManager.getAuthKey());
        setLoading(false);
        return;
      }

      setIsAuthenticated(true);
      setUser(authData.user);
      // NOTE: SignalR will be initialized lazily when connectSignalR is called
    }
    setLoading(false);
  }, [isResolved, isCompanyInitializing]);

  const initializeSignalR = async (dispatcherId) => {
    const hubUrl = company?.signalrUrl;
    if (!hubUrl) {
      console.warn('⚠️ No SignalR hub URL available — company not resolved yet');
      return;
    }

    // Store dispatcher ID for reconnection
    dispatcherIdRef.current = dispatcherId;

    // Tear down any existing connection first
    if (connectionRef.current) {
      try {
        connectionRef.current.off('ReceiveMessage');
        connectionRef.current.off('MessageMarkedAsRead');
        connectionRef.current.off('ForceLogout');
        await connectionRef.current.stop();
      } catch (e) { /* ignore teardown errors */ }
      connectionRef.current = null;
      setSignalRConnection(null);
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        // CRITICAL: Use a function that fetches a FRESH token each time
        accessTokenFactory: () => tokenManager.getToken() || ''
      })
      .withAutomaticReconnect([0, 1000, 2000, 5000, 10000, 30000, 60000])
      .configureLogging(signalR.LogLevel.Error)
      .build();

    // Force logout when the server fires the dispatcher
    connection.on('ForceLogout', () => {
      console.warn('⚠️ ForceLogout received — account deactivated by admin');
      forceLogout();
    });

    // IMPORTANT: Re-register dispatcher after automatic reconnection
    connection.onreconnected(async (connectionId) => {
      console.log('%c🔄 SignalR reconnected - re-registering dispatcher', 'color: green; font-weight: bold');
      reconnectAttemptsRef.current = 0;
      isReconnectingRef.current = false;

      // Retry RegisterDispatcher up to 3 times — if it fails, the dispatcher won't be in the
      // "dispatchers" group and will miss all driver location broadcasts.
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          await connection.invoke('RegisterDispatcher', String(dispatcherId));
          console.log('%c✅ Dispatcher re-registered successfully', 'color: green');
          break;
        } catch (err) {
          console.error(`❌ RegisterDispatcher attempt ${attempt}/${maxRetries} failed:`, err);
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }
        }
      }
    });

    connection.onreconnecting((error) => {
      console.log('%c⚠️ SignalR connection lost, attempting to reconnect...', 'color: orange; font-weight: bold', error);
    });

    // CRITICAL: When withAutomaticReconnect gives up and closes, start manual reconnection
    connection.onclose((error) => {
      console.log('%c❌ SignalR connection closed permanently', 'color: red; font-weight: bold', error);
      // Only attempt manual reconnect if dispatcher is still logged in
      if (dispatcherIdRef.current && !isReconnectingRef.current) {
        console.log('%c🔄 Starting manual reconnection loop...', 'color: orange');
        scheduleManualReconnect();
      }
    });

    try {
      await connection.start();
      await connection.invoke('RegisterDispatcher', String(dispatcherId));

      connectionRef.current = connection;
      reconnectAttemptsRef.current = 0;
      isReconnectingRef.current = false;
      setSignalRConnection(connection);
      console.log('%c✅ SignalR connected and dispatcher registered', 'color: green; font-weight: bold');
    } catch (err) {
      console.error('%c⚠️ SignalR hub not available', 'color: orange; font-weight: bold', err);
      connectionRef.current = null;
      setSignalRConnection(null);
      // Schedule reconnection attempt
      if (dispatcherIdRef.current) {
        scheduleManualReconnect();
      }
    }
  };

  /**
   * Manual reconnection with escalating delays.
   * Runs indefinitely until connection is restored or user logs out.
   */
  const scheduleManualReconnect = () => {
    if (isReconnectingRef.current) return;
    isReconnectingRef.current = true;

    const delays = [2000, 5000, 10000, 30000, 60000]; // escalating delays, caps at 60s

    const tryReconnect = async () => {
      if (!dispatcherIdRef.current) {
        isReconnectingRef.current = false;
        return; // User logged out
      }

      reconnectAttemptsRef.current++;
      const delayIdx = Math.min(reconnectAttemptsRef.current - 1, delays.length - 1);
      const delay = delays[delayIdx];

      console.log(`🔄 Manual reconnect attempt ${reconnectAttemptsRef.current} in ${delay / 1000}s...`);

      reconnectTimerRef.current = setTimeout(async () => {
        if (!dispatcherIdRef.current) {
          isReconnectingRef.current = false;
          return;
        }

        try {
          // Try to reinitialize completely with fresh token and connection
          isReconnectingRef.current = false; // Allow initializeSignalR to proceed
          await initializeSignalR(dispatcherIdRef.current);

          if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
            console.log('%c✅ Manual reconnection succeeded!', 'color: green; font-weight: bold');
            reconnectAttemptsRef.current = 0;
            return; // Success!
          }
        } catch (err) {
          console.warn(`❌ Manual reconnect attempt ${reconnectAttemptsRef.current} failed:`, err.message);
        }

        // Failed - try again
        isReconnectingRef.current = true;
        tryReconnect();
      }, delay);
    };

    tryReconnect();
  };

  const stopSignalR = async () => {
    // Cancel any pending reconnection attempts
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    isReconnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    dispatcherIdRef.current = null;

    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
      } catch (e) { /* ignore */ }
      connectionRef.current = null;
    }
    setSignalRConnection(null);
  };

  /**
   * Ensure SignalR is connected. Called from dashboard on mount and on tab focus.
   * If connection is dead/disconnected, tears it down and reinitializes.
   */
  const connectSignalR = useCallback(async () => {
    // If already connected and healthy, do nothing
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      return connectionRef.current;
    }

    const currentUser = user || JSON.parse(localStorage.getItem(tokenManager.getAuthKey()) || '{}')?.user;
    if (currentUser?.userId) {
      console.log('%c🔌 connectSignalR: Connection is not active, reinitializing...', 'color: orange');
      await initializeSignalR(currentUser.userId);
    }
    return connectionRef.current || null;
  }, [user]);

  /**
   * CRITICAL: Browser visibility + focus listeners.
   * When the user comes back to the tab (even after days), this checks
   * the SignalR connection and reconnects if needed.
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('%c👁️ Tab became visible - checking SignalR connection...', 'color: blue');
        // Use setTimeout(0) to let the browser fully wake up
        setTimeout(() => {
          if (connectionRef.current?.state !== signalR.HubConnectionState.Connected) {
            console.log('%c🔄 Connection is stale, reconnecting...', 'color: orange');
            reconnectAttemptsRef.current = 0; // Reset attempts on visibility change
            isReconnectingRef.current = false;
            connectSignalR();
          } else {
            console.log('%c✅ SignalR still connected', 'color: green');
          }
        }, 100);
      }
    };

    const handleFocus = () => {
      console.log('%c🎯 Window focused - checking SignalR connection...', 'color: blue');
      setTimeout(() => {
        if (connectionRef.current?.state !== signalR.HubConnectionState.Connected) {
          console.log('%c🔄 Connection is stale on focus, reconnecting...', 'color: orange');
          reconnectAttemptsRef.current = 0;
          isReconnectingRef.current = false;
          connectSignalR();
        }
      }, 100);
    };

    // Also periodically check connection health (every 30 seconds)
    const healthCheckInterval = setInterval(() => {
      if (dispatcherIdRef.current && connectionRef.current?.state !== signalR.HubConnectionState.Connected) {
        console.log('%c⏰ Health check: SignalR disconnected, reconnecting...', 'color: orange');
        reconnectAttemptsRef.current = 0;
        isReconnectingRef.current = false;
        connectSignalR();
      }
    }, 30000);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      clearInterval(healthCheckInterval);
    };
  }, [isAuthenticated, connectSignalR]);

  const login = async (username, password) => {
    if (!username || !password) {
      return false;
    }

    try {

      const data = await authAPI.login('dispatcher', username, password);

      // Handle new JWT response format
      const userDetails = data.userDetails || data;
      const userData = {
        username: userDetails.userName || username,
        role: data.userType || 'dispatcher',
        userId: data.userId || userDetails.id,
        token: data.token,
        isAdmin: data.isAdmin || userDetails.isAdmin || false,
        name: data.name || userDetails.name,
        email: userDetails.email,
        phoneNumber: userDetails.phoneNumber
      };

      setIsAuthenticated(true);
      setUser(userData);
      Sentry.setUser({ id: String(userData.userId), username: userData.username, email: userData.email });
      localStorage.setItem(tokenManager.getAuthKey(), JSON.stringify({ user: userData }));

      // CRITICAL: Store JWT token using namespaced key
      if (userData.token) {
        tokenManager.setToken(userData.token);
      } else {
        console.error('⚠️ No token in login response!');
      }

      // Initialize SignalR connection after successful login
      await initializeSignalR(userData.userId);

      return true;
    } catch (error) {
      return false;
    }
  };

  const logout = async () => {
    try {
      if (user?.userId) {
        await authAPI.logout('dispatcher', user.userId);
      }

      // Stop SignalR connection and all reconnection attempts
      await stopSignalR();

      Sentry.setUser(null);
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem(tokenManager.getAuthKey());
      tokenManager.removeToken();
      return true;
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout API fails, clear local state
      await stopSignalR();
      Sentry.setUser(null);
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem(tokenManager.getAuthKey());
      tokenManager.removeToken();
      return true;
    }
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    loading,
    signalRConnection,
    connectSignalR, // Call this from dashboard to establish SignalR connection
    forceLogout // For API interceptor to call (defined above)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};